import { Alert, Badge, Button, Card, Group, Stack, Text, Title } from '@mantine/core';
import { IconAlertTriangle, IconCheck, IconDeviceFloppy, IconRefresh, IconRobot } from '@tabler/icons-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import useFetch from '../helpers/useFetch';
import { setDomain } from '../store/domainSlice';
import BrandKitSection from './BrandContext/BrandKitSection';
import BrandPreview from './BrandContext/BrandPreview';
import BrandVoiceSection from './BrandContext/BrandVoiceSection';
import {
  brandContextPatch,
  brandIdentityStatusOf,
  failureMessage,
  fieldOfError,
  invalidColorFields,
  isRunning,
  isVisualOnlyRun,
  previewPalette,
  rescanFailureMessage,
  touchedSourceKeys,
  VOICE_FIELDS,
} from './BrandContext/brandIdentity';
import ProposalBanner from './BrandContext/ProposalBanner';
import ReadingWebsiteProgress from './BrandContext/ReadingWebsiteProgress';
import classes from './BrandContextForm.module.css';
import ErrorMessage from './ErrorMessage';
import Metadata from './Metadata';

export const POLL_INTERVAL_MS = 2500;
// Reading a site normally takes well under this; past it the page stops asking and says so.
export const POLL_LIMIT_MS = 3 * 60 * 1000;

const STATUS_BADGES = {
  missing: { color: 'gray', label: 'Not set up' },
  reading: { color: 'blue', label: 'Reading' },
  proposed: { color: 'yellow', label: 'Waiting for your review' },
  needsDarkLogo: { color: 'green', label: 'Confirmed' },
  confirmed: { color: 'green', label: 'Confirmed' },
};

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// Only the parts a client edits here, so a poll that only changes metadata leaves the draft alone.
const editableKey = (brandContext) =>
  JSON.stringify([...VOICE_FIELDS.map((field) => brandContext?.[field] ?? null), brandContext?.visual ?? null]);

// The domain fields that go with a brand row: its readiness, worked out the way the domain GET does.
const withStatus = (brandContext) => ({ brandContext, brandIdentityStatus: brandIdentityStatusOf(brandContext) });

const siteName = (url) => {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return 'your website';
  }
};

const BrandContextForm = ({ onSave, onSkip, scope }) => {
  const fetch = useFetch();
  const dispatch = useDispatch();
  const store = useStore();
  const { domain } = useSelector((state) => state.domain);
  const domainUrl = `/api/admin/accounts/${domain.accountId}/domains/${domain.id}`;
  const brandContextUrl = `${domainUrl}/brandContext`;
  const saved = domain.brandContext;
  const confirmScope = scope === 'confirm';

  const [draft, setDraft] = useState(saved);
  const [logoUrl, setLogoUrl] = useState(domain.logoUrl || '');
  const [polling, setPolling] = useState(saved?.status === 'generating' ? 'full' : null);
  const [timedOut, setTimedOut] = useState(false);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState();
  const [fieldErrors, setFieldErrors] = useState({});
  const [notice, setNotice] = useState();
  // The row as read on mount: undefined until then, so onboarding knows whether one exists.
  const [loadedRow, setLoadedRow] = useState();
  const autoStarted = useRef(false);
  // A re-scan that failed while the client watched; a stale visualStatus from an older one is not news.
  const [rescanFailedHere, setRescanFailedHere] = useState(false);
  // visualSources keys the client has since changed by hand: their value no longer came from the site.
  const [editedSources, setEditedSources] = useState(() => new Set());
  // The color role ('colors.text', 'darkColors.link') under the pointer or in focus: the preview
  // outlines everything painted with it, so the client knows which role is which.
  const [hoverRole, setHoverRole] = useState(null);
  const [focusRole, setFocusRole] = useState(null);
  const activeRole = hoverRole ?? focusRole;
  const roleHandlers = useCallback(
    (token) => ({
      onMouseEnter: () => setHoverRole(token),
      onMouseLeave: () => setHoverRole((prev) => (prev === token ? null : prev)),
      onFocus: () => setFocusRole(token),
      onBlur: () => setFocusRole((prev) => (prev === token ? null : prev)),
    }),
    []
  );
  // The header's actions scrolled out of view: repeat them in a bar stuck to the bottom.
  const [headerEl, setHeaderEl] = useState(null);
  const [headerInView, setHeaderInView] = useState(true);
  useEffect(() => {
    if (!headerEl || typeof IntersectionObserver === 'undefined') {
      return undefined;
    }
    const observer = new IntersectionObserver(([entry]) => setHeaderInView(entry.isIntersecting));
    observer.observe(headerEl);
    return () => observer.disconnect();
  }, [headerEl]);

  const savedKey = editableKey(saved);
  useEffect(() => {
    setDraft(saved);
    // `saved` is read through its key on purpose: see editableKey.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedKey, saved?.id]);

  useEffect(() => {
    setLogoUrl(domain.logoUrl || '');
  }, [domain.logoUrl]);

  // Always against the latest domain in the store: a poll or save may land after other changes.
  const syncDomain = useCallback(
    (patch) => dispatch(setDomain({ ...store.getState().domain.domain, ...patch })),
    [dispatch, store]
  );
  const syncBrandContext = useCallback((brandContext) => syncDomain(withStatus(brandContext)), [syncDomain]);
  const refreshDomain = useCallback(
    async (brandContext, isLive = () => true) => {
      try {
        const { domain: fresh } = (await fetch(domainUrl)) || {};
        if (isLive() && fresh) {
          syncDomain({ ...fresh, ...withStatus(brandContext) });
        }
      } catch {
        // The brand row is already in; the logo shows after the next page load.
      }
    },
    [domainUrl, fetch, syncDomain]
  );

  // The domain GET carries only part of the run's metadata (no visualStatus); read the row itself.
  useEffect(() => {
    let live = true;
    fetch(brandContextUrl)
      .then(({ brandContext }) => {
        if (!live) {
          return;
        }
        syncBrandContext(brandContext ?? null);
        setLoadedRow(brandContext ?? null);
        if (brandContext?.status === 'generating') {
          setPolling('full');
        } else if (isRunning(brandContext?.promptMetaData?.visualStatus)) {
          setPolling('visual');
        }
      })
      // Unreadable counts as no row: onboarding then tries a run, which reports its own error.
      .catch(() => live && setLoadedRow((prev) => (prev === undefined ? null : prev)));
    return () => {
      live = false;
    };
  }, [brandContextUrl, fetch, syncBrandContext]);

  useEffect(() => {
    if (!polling) {
      return undefined;
    }
    let live = true;
    const startedAt = Date.now();
    const poll = async () => {
      while (live) {
        await sleep(POLL_INTERVAL_MS);
        if (!live) {
          return;
        }
        let brandContext;
        try {
          ({ brandContext } = await fetch(brandContextUrl));
        } catch (err) {
          if (live) {
            setError(err);
            setPolling(null);
          }
          return;
        }
        if (!live) {
          return;
        }
        syncBrandContext(brandContext ?? null);
        const running =
          polling === 'full'
            ? brandContext?.status === 'generating'
            : isRunning(brandContext?.promptMetaData?.visualStatus);
        if (!running) {
          if (polling === 'visual' && brandContext?.promptMetaData?.visualStatus === 'failed') {
            setRescanFailedHere(true);
          }
          if (polling === 'full') {
            setEditedSources(new Set());
          }
          // The run writes the light logo and primary color onto the domain, not the brand row.
          await refreshDomain(brandContext ?? null, () => live);
          if (live) {
            setPolling(null);
          }
          return;
        }
        if (Date.now() - startedAt > POLL_LIMIT_MS) {
          setTimedOut(true);
          setPolling(null);
          return;
        }
      }
    };
    poll();
    return () => {
      live = false;
    };
  }, [polling, fetch, brandContextUrl, refreshDomain, syncBrandContext]);

  const patch = useMemo(() => brandContextPatch(saved, draft), [saved, draft]);
  const logoChanged = logoUrl !== (domain.logoUrl || '');
  const isDirty = Object.keys(patch).length > 0 || logoChanged;

  const run = async (name, action) => {
    setBusy(name);
    setError(undefined);
    setNotice(undefined);
    try {
      await action();
    } catch (err) {
      const field = fieldOfError(err?.message);
      if (field) {
        setFieldErrors({ [field]: err.message });
      }
      setError(err);
    } finally {
      setBusy(null);
    }
  };

  const startReading = () =>
    run('generate', async () => {
      setTimedOut(false);
      setRescanFailedHere(false);
      // An approved brand is never overwritten: only the look is read again, into a proposal.
      const visualOnly = isVisualOnlyRun(saved);
      try {
        const res = await fetch(`${brandContextUrl}/generate${visualOnly ? '?refresh=visual' : ''}`, {
          method: 'POST',
        });
        if (res?.brandContext) {
          syncBrandContext(res.brandContext);
        }
      } catch (err) {
        // 409: a run is already going, which is what was asked for; follow it.
        if (!/already|generating|progress/i.test(err?.message || '')) {
          throw err;
        }
      }
      setPolling(visualOnly ? 'visual' : 'full');
    });

  // Onboarding starts the read itself, once, and only when there is no row at all: a skipped or
  // failed row is the client's to re-scan, not ours.
  useEffect(() => {
    if (confirmScope && loadedRow === null && !saved && !autoStarted.current) {
      autoStarted.current = true;
      startReading();
    }
    // startReading is rebuilt every render; the ref makes this fire once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [confirmScope, loadedRow, saved]);

  const save = (approve) =>
    run(approve ? 'approve' : 'save', async () => {
      const invalid = invalidColorFields(draft?.visual);
      setFieldErrors(invalid);
      if (Object.keys(invalid).length) {
        throw new Error('Some colors are not valid hex colors.');
      }

      const domainPatch = {};
      if (logoChanged) {
        domainPatch.logoUrl = logoUrl || null;
      }
      const primary = patch.visual?.colors?.primary;
      if (primary && primary !== saved?.visual?.colors?.primary && primary !== domain.primaryColor) {
        // Older features read the domain's own primary color; keep it in step with the kit.
        domainPatch.primaryColor = primary;
      }
      if (Object.keys(domainPatch).length) {
        await fetch(domainUrl, { method: 'PUT', body: JSON.stringify(domainPatch) });
        syncDomain(domainPatch);
      }

      const body = { ...patch };
      if (approve) {
        body.approve = true;
      }
      if (Object.keys(body).length && ['skipped', 'failedGeneration'].includes(saved?.status)) {
        body.status = 'finished';
      }
      if (Object.keys(body).length) {
        const res = await fetch(brandContextUrl, { method: 'PUT', body: JSON.stringify(body) });
        syncBrandContext(res.brandContext);
      }
      setRescanFailedHere(false);
      setNotice(approve ? 'Your brand is confirmed.' : 'Saved.');
      onSave?.();
    });

  const decideProposal = (accept) =>
    run(accept ? 'accept' : 'dismiss', async () => {
      const res = await fetch(brandContextUrl, {
        method: 'PUT',
        body: JSON.stringify(accept ? { acceptProposal: true } : { dismissProposal: true }),
      });
      if (accept) {
        setEditedSources(new Set());
      }
      syncBrandContext(res.brandContext);
      if (accept) {
        await refreshDomain(res.brandContext ?? null);
      }
    });

  const skip = () =>
    run('skip', async () => {
      setPolling(null);
      const res = await fetch(brandContextUrl, { method: 'PUT', body: JSON.stringify({ status: 'skipped' }) });
      syncBrandContext(res.brandContext);
      onSkip?.();
    });

  const markEdited = (keys) =>
    setEditedSources((prev) => (keys.every((key) => prev.has(key)) ? prev : new Set([...prev, ...keys])));

  const updateVisual = (change) => {
    setFieldErrors({});
    markEdited(touchedSourceKeys(draft?.visual || {}, change));
    setDraft((prev) => ({ ...prev, visual: { ...(prev?.visual || {}), ...change } }));
  };

  const meta = saved?.promptMetaData || {};
  const status = domain.brandIdentityStatus || brandIdentityStatusOf(saved);
  const generating = saved?.status === 'generating';
  // A row that is still being read is not waiting for review yet.
  const badge = generating ? STATUS_BADGES.reading : STATUS_BADGES[status] || STATUS_BADGES.missing;
  const rescanning = polling === 'visual';
  // Failure metadata outlives the run (a save never clears it), so it only shows while it still
  // describes the page: a full run that failed and was not since confirmed, or a look-only run that
  // failed in front of the client, or left the kit with no colors at all.
  const hasColors = Object.values(saved?.visual?.colors || {}).some(Boolean);
  const fullRunFailed = saved?.status === 'failedGeneration' && !saved.approvedAt;
  const visualFailed =
    !rescanning && meta.visualStatus === 'failed' && (rescanFailedHere || (!hasColors && !saved?.approvedAt));
  // The API records a look-only failure as visualFailureReason; failureReason is the full run's.
  const visualReason = meta.visualFailureReason ?? meta.failureReason;
  let failureText;
  if (fullRunFailed) {
    failureText = failureMessage(meta.failureReason);
  } else if (visualFailed) {
    failureText = hasColors ? rescanFailureMessage(visualReason) : failureMessage(visualReason);
  }
  const sources = Object.fromEntries(
    Object.entries(meta.visualSources || {}).filter(
      ([key]) => !editedSources.has(key) && (key !== 'logoUrl' || logoUrl)
    )
  );

  const visual = draft?.visual || {};
  const { colors: previewColors, derivedDark, dark: previewDark } = previewPalette(visual, domain.primaryColor);

  const reviewing = !!saved && !(generating && !timedOut);
  // Only say we read the site when the read brought something back.
  const readNothing = fullRunFailed || saved?.status === 'skipped' || (visualFailed && !hasColors);
  let intro = 'How your emails, pop-ups and assistant look and sound.';
  if (confirmScope && reviewing) {
    intro = readNothing
      ? 'Add your logo, colors and how you sound, then confirm.'
      : `We read ${siteName(domain.url)}. Check it looks and sounds like you, then confirm.`;
  }

  const actions = reviewing && (
    <Group gap="sm" justify="flex-end" wrap="wrap">
      {notice && (
        <Text size="sm" c="green">
          {notice}
        </Text>
      )}
      {/* Skipping a confirmed brand would un-confirm it. */}
      {confirmScope && !saved.approvedAt && (
        <Button variant="subtle" onClick={skip} loading={busy === 'skip'} disabled={!!busy}>
          Skip for now
        </Button>
      )}
      {!confirmScope && (
        <Button
          variant={saved.approvedAt ? 'filled' : 'default'}
          leftSection={<IconDeviceFloppy size={16} />}
          onClick={() => save(false)}
          disabled={!isDirty || !!busy}
          loading={busy === 'save'}
        >
          {isDirty ? 'Save' : 'Saved'}
        </Button>
      )}
      {(confirmScope || !saved.approvedAt) && (
        <Button
          leftSection={<IconCheck size={16} />}
          onClick={() => save(true)}
          loading={busy === 'approve'}
          disabled={!!busy}
        >
          This is our brand
        </Button>
      )}
    </Group>
  );

  const header = (
    <Group justify="space-between" align="flex-start" mb="md" wrap="wrap" ref={setHeaderEl}>
      <div>
        <Group gap="sm">
          <Title order={2}>Brand Identity</Title>
          <Badge color={badge.color} variant="light">
            {badge.label}
          </Badge>
        </Group>
        <Text size="sm" c="dimmed" mt={4}>
          {intro}
          {saved?.approvedAt && ` Confirmed on ${new Date(saved.approvedAt).toLocaleDateString()}.`}
        </Text>
      </div>
      <Group gap="sm" wrap="wrap" justify="flex-end">
        {saved && !generating && (
          <Button
            variant="subtle"
            color="gray"
            leftSection={<IconRefresh size={16} />}
            onClick={startReading}
            loading={busy === 'generate' || rescanning}
            disabled={isDirty}
            title={isDirty ? 'Save or undo your changes first' : undefined}
          >
            Re-scan website
          </Button>
        )}
        {actions}
      </Group>
    </Group>
  );

  const shell = (children) => (
    <div>
      <Metadata title="Brand Identity" />
      {header}
      {children}
    </div>
  );

  // Onboarding with no row yet: the read is about to start, or is starting.
  if (!saved && confirmScope && !error) {
    return shell(<ReadingWebsiteProgress url={domain.url} />);
  }

  if (!saved) {
    return shell(
      <>
        <ErrorMessage message={error?.message} />
        <Card padding="lg" radius="md" withBorder>
          <Stack align="center" gap="sm" ta="center">
            <IconRobot size={40} />
            <Title order={4}>We haven&apos;t read your website yet</Title>
            <Text size="sm" c="dimmed">
              We&apos;ll look at {domain.url || 'your website'} and fill in your logo, colors and the way you talk to
              customers. You review it before anything uses it.
            </Text>
            <Button onClick={startReading} loading={busy === 'generate'}>
              Read my website
            </Button>
          </Stack>
        </Card>
      </>
    );
  }

  if (generating && !timedOut) {
    return shell(
      <>
        <ErrorMessage message={error?.message} />
        <ReadingWebsiteProgress url={domain.url} onSkip={confirmScope ? skip : undefined} skipping={busy === 'skip'} />
      </>
    );
  }

  return shell(
    <Stack gap="md">
      <ErrorMessage message={error?.message} />

      {timedOut && (
        <Alert color="yellow" icon={<IconAlertTriangle size={16} />} title="This is taking longer than usual">
          <Text size="sm">
            We are still reading your website. Check again in a few minutes. If you fill in your brand below now, what
            we find when the reading finishes may replace it.
          </Text>
          <Button
            size="xs"
            mt="xs"
            variant="light"
            onClick={() => {
              setTimedOut(false);
              setPolling(isVisualOnlyRun(saved) ? 'visual' : 'full');
            }}
          >
            Check again
          </Button>
        </Alert>
      )}

      {failureText && (
        <Alert color="red" variant="light" icon={<IconAlertTriangle size={16} />} data-testid="failure">
          {failureText}
        </Alert>
      )}

      {status === 'needsDarkLogo' && (
        <Alert color="blue" variant="light" data-testid="dark-logo-reminder">
          Your brand is confirmed. One more thing when you have it: add a logo for dark backgrounds, so your emails look
          right in dark-mode inboxes.
        </Alert>
      )}

      {saved.status === 'skipped' && (
        <Alert color="gray" variant="light">
          You skipped this step. Fill in your brand below, or use Re-scan website and we&apos;ll read it for you.
        </Alert>
      )}

      {rescanning && (
        <Alert color="blue" variant="light">
          Looking at your website again. Your saved brand stays as it is until you choose to use what we find.
        </Alert>
      )}

      {meta.visualProposal && (
        <ProposalBanner
          proposal={meta.visualProposal}
          busy={['accept', 'dismiss'].includes(busy) ? busy : null}
          onAccept={() => decideProposal(true)}
          onDismiss={() => decideProposal(false)}
        />
      )}

      <div className={classes.layout}>
        <Stack gap="md" miw={0}>
          <Card withBorder padding="lg" radius="md">
            <BrandVoiceSection brandContext={draft} setBrandContext={setDraft} />
          </Card>
          <Card withBorder padding="lg" radius="md">
            <BrandKitSection
              visual={visual}
              logoUrl={logoUrl}
              sources={sources}
              previewColors={previewColors}
              derivedDark={derivedDark}
              darkPalette={previewDark}
              fieldErrors={fieldErrors}
              onVisualChange={updateVisual}
              onLogoChange={(url) => {
                markEdited(['logoUrl']);
                setLogoUrl(url);
              }}
              activeRole={activeRole}
              roleHandlers={roleHandlers}
            />
          </Card>
          {/* Inside the editor column, so it never covers the pinned preview. */}
          {!headerInView && <div className={`${classes.actionBar} ${classes.actionBarWide}`}>{actions}</div>}
        </Stack>
        <aside className={classes.aside} aria-label="Preview">
          <Card withBorder padding="md" radius="md">
            <Title order={4} fw={600}>
              Preview
            </Title>
            <Text size="xs" c="dimmed" mb="sm">
              Point at or select a color to see where it is used.
            </Text>
            <BrandPreview
              colors={previewColors}
              darkColors={previewDark}
              logoUrl={logoUrl}
              logoDarkUrl={visual.logoDarkUrl}
              name={domain.name || 'Your brand'}
              fontFamily={visual.fontFamily}
              webFontUrl={visual.webFontUrl}
              radius={visual.radius}
              activeRole={activeRole}
            />
          </Card>
        </aside>
      </div>

      {/* Narrow screens stack the preview under the editor, so the bar follows the whole page there. */}
      {!headerInView && <div className={`${classes.actionBar} ${classes.actionBarNarrow}`}>{actions}</div>}
    </Stack>
  );
};

export default BrandContextForm;
